/**
 * @fileoverview Test Suite Index
 *
 * Exports all available test suites for the integration testing system.
 * Supports "flipped" test generation that mirrors tests for Dark player perspective.
 */

export { basicMoveTests, basicMovesSuite, specialMoveTests, specialMovesSuite } from "./basicMoves";
export { captureTests, capturesSuite } from "./captures";
export { bonusTurnTests, bonusTurnsSuite } from "./bonusTurns";
export { victoryTests, victoriesSuite } from "./victories";
export { statusEffectTests, statusEffectsSuite, superComplexTests, superComplexSuite } from "./statusEffects";
export { delayedEffectTests, delayedEffectsSuite, delayedEffectSequences } from "./delayedEffects";
export { gameOverSuite } from "./gameOver";
export { matchmakingTests, matchmakingSuite, matchmakingServiceTests, matchmakingServiceSuite, clockServiceSuite } from "./matchmaking";
export { ALL_AI_DECISION_SUITES, ALL_AI_DECISION_TESTS, FORCED_LINE_TESTS } from "./aiDecisions";

import { TestSuite, MatchmakingTestSuite } from "../types";
import { basicMovesSuite, specialMovesSuite } from "./basicMoves";
import { capturesSuite } from "./captures";
import { bonusTurnsSuite } from "./bonusTurns";
import { victoriesSuite } from "./victories";
import { statusEffectsSuite, superComplexSuite } from "./statusEffects";
import { delayedEffectsSuite } from "./delayedEffects";
import { gameOverSuite } from "./gameOver";
import { matchmakingServiceSuite, clockServiceSuite } from "./matchmaking";
import { flipTest } from "../TestFlipper";

/**
 * All available test suites (Light perspective only)
 * These are engine tests that run through the game logic
 */
export const ALL_TEST_SUITES: TestSuite[] = [
  basicMovesSuite,
  specialMovesSuite,
  capturesSuite,
  bonusTurnsSuite,
  victoriesSuite,
  statusEffectsSuite,
  delayedEffectsSuite,
  gameOverSuite,
  superComplexSuite,
];

/**
 * All matchmaking test suites (automated service-level tests)
 * These test Firebase services directly
 */
export const ALL_MATCHMAKING_SUITES: MatchmakingTestSuite[] = [
  matchmakingServiceSuite,
  clockServiceSuite,
];

/**
 * Generate a flipped version of a test suite (Dark perspective)
 */
export function flipSuite(suite: TestSuite): TestSuite {
  return {
    name: `${suite.name} (Dark Perspective)`,
    description: `${suite.description} - Flipped to test from Dark's starting position`,
    tests: suite.tests.map(flipTest),
  };
}

/**
 * Get all test suites including flipped versions
 * This effectively doubles test coverage by testing both perspectives
 */
export function getAllTestSuitesWithFlipped(): TestSuite[] {
  const allSuites: TestSuite[] = [];

  for (const suite of ALL_TEST_SUITES) {
    allSuites.push(suite);
    allSuites.push(flipSuite(suite));
  }

  return allSuites;
}

/**
 * Get a test suite by name
 */
export function getTestSuite(name: string): TestSuite | undefined {
  return ALL_TEST_SUITES.find((suite) => suite.name === name);
}

/**
 * Get all test suite names
 */
export function getTestSuiteNames(): string[] {
  return ALL_TEST_SUITES.map((suite) => suite.name);
}

/**
 * Get total test count (original + flipped)
 */
export function getTotalTestCount(includeFlipped: boolean = false): number {
  const baseCount = ALL_TEST_SUITES.reduce((sum, suite) => sum + suite.tests.length, 0);
  return includeFlipped ? baseCount * 2 : baseCount;
}
